<script>
import moment from "moment";
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "HRM",
      text1: "Payroll Report",
      clientAdmin: clientAdmin(),
      filter: {
        month: Number(moment().format('M')),
        year: Number(moment().format('YYYY')),
        department_id: '',
        employee_id: '',
        status: '',
        page: 1,
      },
      months: [
        { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
        { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
        { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
        { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' },
      ],
      statuses: [
        { id: 'pending', name: 'Pending' },
        { id: 'paid', name: 'Paid' },
        { id: 'on_hold', name: 'On Hold' },
        { id: 'processed', name: 'Processed' },
      ],
      years: [],
      summary: {},
      data: [],
      pagination: {},
      noDataMessage: '',
      departments: [],
      employees: [],
    };
  },

  mounted() {
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.years.push({ id: y, name: String(y) });
    }
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv', ...this.filter };
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/hrms/reports/payroll?${query_string}`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payroll-report-${this.filter.month}-${this.filter.year}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(resetPage = true) {
      this.errors = {};
      if (resetPage) this.filter.page = 1;
      this.setLoading(true);
      try {
        const query_string = new URLSearchParams(this.filter).toString();
        const response = await axios.get(`/hrms/reports/payroll?${query_string}`);

        if (!response.data.status) {
          this.summary = {};
          this.data = [];
          this.pagination = {};
          this.noDataMessage = response.data.message || 'No records found.';
          return;
        }

        this.noDataMessage = '';
        this.summary = response.data.data.summary || {};
        const rows = response.data.data.rows || {};
        this.data = rows.data || [];
        this.pagination = rows;
      } catch (error) {
        this.summary = {};
        this.data = [];
        this.pagination = {};
        this.noDataMessage = 'No records found.';
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    goToPage(page) {
      if (!page || page === this.pagination.current_page) return;
      this.filter.page = page;
      this.filterReport(false);
    },

    async getDropdowns() {
      try {
        const [deptRes, empRes] = await Promise.allSettled([
          axios.get('/hrms/departments/dropdown'),
          axios.get('/hrms/employees', { params: { per_page: 1000 } }),
        ]);
        this.departments = deptRes.status === 'fulfilled' ? deptRes.value.data.data : [];
        this.employees = empRes.status === 'fulfilled' ? empRes.value.data.data.data : [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    hasActiveFilter() {
      const currentMonth = String(moment().month() + 1);
      const currentYear = String(moment().year());
      return this.filter.department_id || this.filter.employee_id || this.filter.status
        || String(this.filter.month) !== currentMonth || String(this.filter.year) !== currentYear;
    },

    clearFilter() {
      this.filter.department_id = '';
      this.filter.employee_id = '';
      this.filter.status = '';
      this.filter.month = moment().format('M');
      this.filter.year = moment().format('YYYY');
      this.filterReport();
    },

    employeeName(row) {
      const name = row.employee?.full_name;
      return (name && name.trim()) || row.employee?.code || '-';
    },

    formatNum(val) {
      const n = parseFloat(val || 0);
      return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
    },

    formatCurrency(val) {
      const n = parseFloat(val || 0);
      return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    statusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'paid' || s === 'processed') return 'bg-success';
      if (s === 'pending') return 'bg-warning';
      if (s === 'on_hold') return 'bg-danger';
      return 'bg-light text-dark';
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <span class="badge border text-dark me-2" style="font-size:12px;" v-if="summary.run_status">
            Run Status: <strong class="text-capitalize">{{ summary.run_status }}</strong>
          </span>
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Employees</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_employees }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Gross</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.total_gross) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Deductions</span>
              <span class="fw-bold text-warning" style="font-size:20px;">{{ formatCurrency(summary.total_deductions) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unpaid Deduction</span>
              <span class="fw-bold text-danger" style="font-size:20px;">{{ formatCurrency(summary.total_unpaid) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Net</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatCurrency(summary.total_net) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Net Salary</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_net_salary) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Present Days</span>
              <span class="fw-bold text-dark" style="font-size:18px;">{{ formatNum(summary.total_present_days) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Absent Days</span>
              <span class="fw-bold text-dark" style="font-size:18px;">{{ formatNum(summary.total_absent_days) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Paid Leave Days</span>
              <span class="fw-bold text-dark" style="font-size:18px;">{{ formatNum(summary.total_paid_leave) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Gross Change</span>
              <span class="fw-bold" :class="(summary.gross_change_pct || 0) >= 0 ? 'text-success' : 'text-danger'" style="font-size:18px;">
                {{ summary.gross_change_pct != null ? summary.gross_change_pct + '%' : '-' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Status Counts -->
      <div class="d-flex flex-wrap gap-2 mb-3" v-if="summary.status_counts">
        <span class="badge border text-dark" style="font-size:12px;" v-for="(count, key) in summary.status_counts" :key="key">
          <span class="text-capitalize">{{ key.replace('_', ' ') }}</span>: <strong>{{ count }}</strong>
        </span>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.month" :options="months" label="name" :reduce="m => m.id"
                placeholder="Month" @update:modelValue="filterReport()" style="min-width: 140px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.year" :options="years" label="name" :reduce="y => y.id"
                placeholder="Year" @update:modelValue="filterReport()" style="min-width: 120px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.department_id" :options="departments" label="name" :reduce="d => d.id"
                placeholder="Department" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.employee_id" :options="employees" label="full_name" :reduce="e => e.id"
                placeholder="Employee" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.status" :options="statuses" label="name" :reduce="s => s.id"
                placeholder="Status" :clearable="true" @update:modelValue="filterReport()" style="min-width: 160px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                    <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Paid Leave</th>
                    <th>Unpaid Leave</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net Salary</th>
                    <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id || index">
                        <td>{{ (pagination.per_page * (pagination.current_page - 1)) + index + 1 }}.</td>
                        <td>
                            <strong class="text-dark">{{ employeeName(row) }}</strong>
                            <small class="text-muted d-block" style="font-size:11px;">{{ row.employee?.code || '' }}</small>
                        </td>
                        <td>{{ formatNum(row.present_days) }}</td>
                        <td>{{ formatNum(row.absent_days) }}</td>
                        <td>{{ formatNum(row.paid_leave_days) }}</td>
                        <td>{{ formatNum(row.unpaid_leave_days) }}</td>
                        <td>{{ formatCurrency(row.gross_salary) }}</td>
                        <td>{{ formatCurrency(row.total_deductions) }}</td>
                        <td><strong>{{ formatCurrency(row.net_salary) }}</strong></td>
                        <td>
                            <span class="badge rounded-pill" :class="statusClass(row.status)">
                            {{ row.status }}
                            </span>
                        </td>
                        </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">{{ noDataMessage || 'No records found.' }}</div>

              <!-- Pagination -->
              <div class="d-flex align-items-center justify-content-between p-3" v-if="pagination.last_page > 1">
                <span class="text-muted" style="font-size:12px;">
                  Showing {{ pagination.from || 0 }} to {{ pagination.to || 0 }} of {{ pagination.total || 0 }}
                </span>
                <nav>
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page - 1)">Previous</a>
                    </li>
                    <li class="page-item" v-for="p in pagination.last_page" :key="p" :class="{ active: p === pagination.current_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>